/* eslint-disable react/no-danger */
import { useTranslation } from 'next-i18next';
import { Fragment } from 'react';
import { pipe } from 'fp-ts/function';
import { Builder } from '@builder.io/react';
import classNames from 'classnames';

import {
  AuctionCard,
  Scrollable,
  TimerOld as Timer,
} from '@catawiki/cata-style';
import { RemoteData } from '@catawiki/runtime-toolkit';

import { fetchAuctions } from '@/shared/api/buyer';
import {
  BackgroundClassMap,
  BACKGROUND_COLOR_NAMES,
} from '@/shared/constants/colors';
import { cleanBuilderStringInput } from '@/shared/text';

import { FeaturedAuction } from './DayAuction/FeaturedAuctions';
import { formatDayMonth } from '../../formatter';

interface SelectedPartnerAuction {
  logo: string;
  auction: FeaturedAuction;
}

interface BuilderInput {
  title: string;
  subTitle: string;
  backgroundColorClassName: string;
  auctionIds: { logo: string; auctionId: number }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes?: any;
}

type Props = BuilderInput & {
  locale: string;
  selectedPartnerAuctions: SelectedPartnerAuction[];
};

export const SelectedPartner = ({
  locale,
  title,
  subTitle,
  backgroundColorClassName,
  selectedPartnerAuctions = [],
  attributes,
}: Props) => {
  const { t } = useTranslation();
  return (
    <div
      {...attributes}
      className={classNames(backgroundColorClassName, attributes?.className)}
    >
      <div className="u-wrapper">
        <h1
          className="u-typography-h1 u-m-t-xxl-10 u-m-b-xl"
          dangerouslySetInnerHTML={{
            __html: cleanBuilderStringInput(title),
          }}
        />
        <div className="p-selected-partner u-m-b-xxl-6">
          <h6 className="u-typography-h6 u-m-b-xl">
            {cleanBuilderStringInput(subTitle, true)}
          </h6>
          <Scrollable listClassName="tw:items-center u-g-m">
            {selectedPartnerAuctions.map((auction) => {
              const timer = (
                <Timer
                  timer={t('closes_on', {
                    closed_on_date: formatDayMonth(
                      locale,
                      new Date(auction.auction.closeAt)
                    ),
                  })}
                />
              );
              const auctionCardSubTitle = `${t('auction_lane.curated_by')} ${
                auction.auction.curator.name
              }`;
              return (
                <Fragment key={auction.auction.id}>
                  <div className="p-logo u-m-b-m">
                    <img src={auction.logo} alt="partner logo" />
                  </div>
                  <AuctionCard
                    key={auction.auction.id}
                    {...auction.auction}
                    subTitle={auctionCardSubTitle}
                    timer={timer}
                  />
                </Fragment>
              );
            })}
          </Scrollable>
        </div>
      </div>
    </div>
  );
};

export function dataFetcher(locale: string) {
  return async ({ auctionIds = [] }: BuilderInput) => {
    if (auctionIds.length === 0) return [];
    const auctions = pipe(
      await fetchAuctions(
        locale,
        auctionIds.map((item) => item.auctionId)
      ),
      RemoteData.fromEither,
      RemoteData.match({
        idle: () => [],
        loading: () => [],
        success: (data) => data,
        failure: () => [],
      })
    );

    return {
      locale,
      selectedPartnerAuctions: auctions
        .filter(
          (auction) => auction.auctioneers && auction.auctioneers.length > 0
        )
        .map((auction, index) => {
          const featureAuction: FeaturedAuction = {
            id: auction.id,
            href: auction.url,
            title: auction.title,
            curator: auction.auctioneers[0],
            mainImage: auction.promo_images[0].medium,
            thumbImages: auction.promo_images
              .map((item) => item.medium)
              .slice(1, 4) as [string, string, string],
            numberOfLots: auction.lot_count,
            closeAt: new Date(auction.close_at).getTime(),
          };
          return {
            logo: auctionIds[index].logo,
            auction: featureAuction,
          };
        }),
    };
  };
}

export function componentRegister() {
  const name = 'SelectedPartner';
  Builder.registerComponent(SelectedPartner, {
    name,
    inputs: [
      {
        name: 'title',
        type: 'string',
      },
      {
        name: 'subTitle',
        friendlyName: 'Sub Title',
        type: 'string',
      },
      {
        name: 'backgroundColorClassName',
        friendlyName: 'Background Color',
        type: 'string',
        defaultValue: 'u-bgcolor-light-gray',
        enum: BACKGROUND_COLOR_NAMES.map((value) => ({
          label: value,
          value: BackgroundClassMap[value],
        })),
      },
      {
        name: 'auctionIds',
        type: 'list',
        subFields: [
          { name: 'logo', type: 'file', allowedFileTypes: ['jpeg', 'png'] },
          { name: 'auctionId', type: 'number' },
        ],
      },
    ],
    noWrap: true,
  });
  return name;
}
